import React, { useMemo } from 'react';
import MapboxMiniMap from '../maps/MapboxMiniMap';
import { RWANDA_DEFAULTS, calculateCenter, getFacilityColor } from '../../utils/geoUtils';

interface Facility {
    id: number;
    name: string;
    type: string;
    location_lat: number;
    location_lng: number;
    capacity_kg: number;
    current_usage_kg: number;
    status: string;
}

interface Props {
    facilities: Facility[];
    onFacilityClick: (facility: Facility) => void;
}

const WarehouseMapDashboard: React.FC<Props> = ({ facilities, onFacilityClick }) => {
    // Filter facilities to only include those with valid coordinates
    const facilitiesWithCoords = facilities.filter(f =>
        f.location_lat != null &&
        f.location_lng != null &&
        !isNaN(f.location_lat) &&
        !isNaN(f.location_lng)
    );

    // Calculate map center from facilities with coordinates or use Rwanda default
    const center = facilitiesWithCoords.length > 0
        ? calculateCenter(facilitiesWithCoords.map(f => ({ lat: Number(f.location_lat), lng: Number(f.location_lng) })))
        : RWANDA_DEFAULTS.center;

    const mapMarkers = useMemo(() => facilitiesWithCoords.map(f => ({
        id: f.id,
        position: [f.location_lat, f.location_lng] as [number, number],
        color: getFacilityColor(f.type),
        label: f.name,
        onClick: () => onFacilityClick(f),
    })), [facilitiesWithCoords, onFacilityClick]);

    const mapCenter: [number, number] = [center.lat, center.lng];

    return (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 relative isolate">
            <div className="bg-gradient-to-r from-[#0089bf] to-blue-600 px-5 py-4 border-b border-white/10">
                <div className="flex items-center justify-between gap-3">
                    <h2 className="text-lg md:text-xl font-bold text-white">
                        Warehouse Facilities Map
                    </h2>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm text-white font-medium">
                        {facilities.length} facilities
                    </span>
                </div>
                <p className="text-blue-100 text-sm mt-1">Click a marker to open facility details</p>
            </div>

            <div style={{ height: '440px', width: '100%' }} className="overflow-hidden">
                {facilitiesWithCoords.length > 0 ? (
                    <MapboxMiniMap
                        center={mapCenter}
                        zoom={facilitiesWithCoords.length === 1 ? 14 : 11}
                        scrollWheelZoom={true}
                        markers={mapMarkers}
                        fitBounds={facilitiesWithCoords.length > 1}
                    />
                ) : facilities.length > 0 ? (
                    <div className="flex items-center justify-center h-full bg-gray-50">
                        <div className="text-center max-w-md">
                            <div className="text-6xl mb-4">📍</div>
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">GPS Coordinates Missing</h3>
                            <p className="text-gray-500 mb-4">
                                {facilities.length} {facilities.length === 1 ? 'facility exists' : 'facilities exist'} but {facilities.length === 1 ? 'doesn\'t' : 'don\'t'} have GPS coordinates set.
                            </p>
                            <p className="text-sm text-gray-600">
                                Edit the {facilities.length === 1 ? 'facility' : 'facilities'} to add location coordinates, or create a new facility with GPS data.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full bg-gray-50">
                        <div className="text-center">
                            <div className="text-6xl mb-4">📍</div>
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Facilities Yet</h3>
                            <p className="text-gray-500">Create your first storage facility to see it on the map</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Legend */}
            {facilitiesWithCoords.length > 0 && (
                <div className="bg-gray-50 px-5 py-3 border-t flex flex-wrap items-center justify-between gap-3 text-sm">
                    <div className="flex items-center flex-wrap gap-x-5 gap-y-2">
                        <span className="font-medium text-gray-700">Legend:</span>
                        <div className="flex items-center">
                            <span className="text-lg mr-1">❄️</span>
                            <span className="text-gray-600">Cold Room</span>
                        </div>
                        <div className="flex items-center">
                            <span className="text-lg mr-1">🏗️</span>
                            <span className="text-gray-600">Insulated Shed</span>
                        </div>
                        <div className="flex items-center">
                            <span className="text-lg mr-1">📦</span>
                            <span className="text-gray-600">Ambient Storage</span>
                        </div>
                    </div>
                    <div className="text-gray-500">
                        📍 {facilitiesWithCoords.length} {facilitiesWithCoords.length === 1 ? 'facility' : 'facilities'} on map
                    </div>
                </div>
            )}
        </div>
    );
};

export default WarehouseMapDashboard;
